import {
  legalDocumentError,
  previewLegalPdf,
  saveLegalPdf,
} from '@chiroup/client-core/functions/legalDocumentClient';
import {
  LegalDocumentAudience,
  LegalDocumentKind,
  LegalDocumentVersion,
} from '@chiroup/core/types/LegalDocument.type';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { legalDocumentService } from '../../services/legalDocument.service';
import ClinicDocumentHistory from './ClinicDocumentHistory';

const inputClass =
  'mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-white';
const buttonClass =
  'rounded-lg bg-primary-600 px-4 py-2.5 text-base font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm';
const linkClass =
  'rounded px-2 py-2.5 font-medium text-primary-600 underline dark:text-primary-300';

const readPdf = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (file.size > 2 * 1024 * 1024) {
      reject(new Error('The PDF must be no larger than 2 MiB.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('The PDF could not be read.'));
    reader.readAsDataURL(file);
  });

const PublishDocument = ({
  version,
  onDone,
}: {
  version: LegalDocumentVersion;
  onDone: () => void;
}) => {
  const [effective, setEffective] = useState('');
  const [material, setMaterial] = useState(true);
  const [reference, setReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [name, setName] = useState('');
  const [signer, setSigner] = useState('');
  const [title, setTitle] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const baa = version.kind === 'baa';
  const publish = async () => {
    setBusy(true);
    setError('');
    try {
      await legalDocumentService.publish(version.id, {
        effectiveAt: new Date(effective).getTime(),
        materialChange: baa || material,
        legalReviewReference: reference,
        confirmPublication: confirmed,
        ...(baa
          ? {
              businessAssociateName: name,
              businessAssociateSigner: signer,
              businessAssociateTitle: title,
              signingAuthorityConfirmed: authority,
            }
          : {}),
      });
      onDone();
    } catch (err) {
      setError(legalDocumentError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="mt-4 space-y-5 rounded-xl border border-amber-600/30 p-5 sm:p-7"
      onSubmit={event => {
        event.preventDefault();
        publish();
      }}
    >
      <h3 className="text-xl font-semibold">
        Publish {version.title} · {version.version}
      </h3>
      <p className="max-w-prose text-amber-800 dark:text-amber-200">
        Publication is permanent. Required changes pause access when effective. Confirm the
        audience, legal text, notice period, and continuity-of-care arrangements before publishing.
        Existing records remain available.
      </p>
      <button
        type="button"
        className={linkClass}
        onClick={async () => {
          try {
            await previewLegalPdf(() => legalDocumentService.download(version.id), version.sha256);
            setReviewed(true);
          } catch (err) {
            setError(legalDocumentError(err));
          }
        }}
      >
        Review exact PDF in a new tab
      </button>
      <p className="break-all font-mono text-sm">SHA-256: {version.sha256}</p>
      <label className="block font-medium" htmlFor={`effective-${version.id}`}>
        Effective date & time (your local time)
        <input
          id={`effective-${version.id}`}
          name="effectiveAt"
          type="datetime-local"
          className={inputClass}
          required
          value={effective}
          onChange={e => setEffective(e.target.value)}
        />
      </label>
      {!baa && (
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            name="materialChange"
            className="mt-1 h-5 w-5 shrink-0 rounded"
            checked={material}
            onChange={e => setMaterial(e.target.checked)}
          />
          Require new {version.kind === 'privacy' ? 'acknowledgment' : 'acceptance'}. Clear only for
          a legally reviewed, nonmaterial change that preserves the prior acceptance requirement.
        </label>
      )}
      {baa && (
        <>
          <p className="font-medium">
            Every replacement BAA requires a new clinic signature. Publishing electronically signs
            this exact version for ChiroUp, effective upon clinic execution.
          </p>
          <label className="block font-medium" htmlFor="baa-entity">
            Business Associate legal entity name
            <input
              id="baa-entity"
              name="businessAssociateName"
              className={inputClass}
              maxLength={200}
              required
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </label>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block font-medium" htmlFor="baa-signer">
              Your full legal name
              <input
                id="baa-signer"
                name="businessAssociateSigner"
                className={inputClass}
                maxLength={200}
                required
                value={signer}
                onChange={e => setSigner(e.target.value)}
              />
            </label>
            <label className="block font-medium" htmlFor="baa-title">
              Your title / capacity
              <input
                id="baa-title"
                name="businessAssociateTitle"
                className={inputClass}
                maxLength={200}
                required
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </label>
          </div>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              name="signingAuthorityConfirmed"
              className="mt-1 h-5 w-5 shrink-0 rounded"
              checked={authority}
              onChange={e => setAuthority(e.target.checked)}
            />
            I have authority to bind the Business Associate identified above. I consent to
            electronic records and signatures and intend my typed name to sign this BAA for that
            entity. I verified that the PDF and execution certificate identify the correct
            contracting parties.
          </label>
        </>
      )}
      <label className="block font-medium" htmlFor="legal-review-reference">
        Legal review / approval reference
        <input
          id="legal-review-reference"
          name="legalReviewReference"
          className={inputClass}
          maxLength={500}
          required
          value={reference}
          onChange={e => setReference(e.target.value)}
        />
      </label>
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          name="confirmPublication"
          className="mt-1 h-5 w-5 shrink-0 rounded"
          checked={confirmed}
          onChange={e => setConfirmed(e.target.checked)}
        />
        I confirm legal approval, the change summary, effective time, and audience. I understand
        that publishing cannot alter previous signatures or erase a release.
      </label>
      {error && (
        <p role="alert" className="text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          className={buttonClass}
          disabled={busy || !reviewed || !confirmed || (baa && !authority)}
        >
          {busy ? 'Publishing…' : baa ? 'Sign for ChiroUp & publish' : 'Publish document'}
        </button>
        <button type="button" className={linkClass} onClick={onDone} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const DocumentCenter = () => {
  const { hasPermission } = useAdminPermissions();
  const catalog = useQuery(['legalDocumentCatalog'], legalDocumentService.catalog);
  const [kind, setKind] = useState<LegalDocumentKind>('terms');
  const [audience, setAudience] = useState<LegalDocumentAudience>('essentials');
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [summary, setSummary] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [useBaa, setUseBaa] = useState(true);
  const [publishing, setPublishing] = useState<LegalDocumentVersion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [historyClinicId, setHistoryClinicId] = useState<number | null>(null);
  const createDraft = async () => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const template = kind === 'baa' && useBaa;
      if (!template && !file) throw new Error('Choose a PDF file.');
      await legalDocumentService.create({
        kind,
        audience: kind === 'baa' ? 'all' : audience,
        title,
        version,
        changeSummary: summary,
        ...(template ? { useExistingBaa: true } : { pdfBase64: await readPdf(file!) }),
      });
      setSuccess('Draft saved. Review the PDF, then publish when legal approval is complete.');
      setVersion('');
      await catalog.refetch();
    } catch (err) {
      setError(legalDocumentError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="mx-auto max-w-6xl px-4 py-8 text-base text-gray-900 dark:text-white sm:px-8 sm:text-sm">
      <h1 className="text-balance text-3xl font-semibold tracking-tight">Document Center</h1>
      <p className="mt-3 max-w-prose text-pretty text-gray-600 dark:text-dark-gray-300">
        Publish clinic agreements and product-specific legal documents. Preserve every version and
        the evidence behind every signature.
      </p>
      <aside className="mt-6 rounded-lg bg-gray-50 p-5 dark:bg-dark-gray-800">
        <h2 className="font-medium">Publication & legal safeguards</h2>
        <p className="mt-2 max-w-prose">
          A privacy acknowledgment records notice delivery; it does not authorize optional
          processing. New purposes may need separate consent and legal review. A BAA does not
          replace a GDPR processing agreement or applicable Canadian health privacy obligations.
        </p>
        <p className="mt-2 max-w-prose">
          Schedule changes with appropriate notice. New requirements block clinic access when
          effective. All five streams must be reviewed for rollout: Essentials terms and privacy,
          EHR terms and privacy, and the clinic BAA.
        </p>
      </aside>
      {(error || catalog.isError) && (
        <p role="alert" className="mt-4 text-red-600 dark:text-red-300">
          {error || 'The document catalog could not be loaded.'}
          <button type="button" className={linkClass} onClick={() => catalog.refetch()}>
            Retry
          </button>
        </p>
      )}
      {success && (
        <p role="status" className="mt-4 text-green-700 dark:text-green-300">
          {success}
        </p>
      )}
      {hasPermission('documents.publish') && (
        <section className="mt-8 border-t border-gray-900/10 pt-6 dark:border-white/10">
          <h2 className="text-xl font-semibold">Prepare a new version</h2>
          <form
            className="mt-5 space-y-5"
            onSubmit={event => {
              event.preventDefault();
              createDraft();
            }}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block font-medium" htmlFor="document-kind">
                Document
                <select
                  id="document-kind"
                  name="kind"
                  className={inputClass}
                  value={kind}
                  onChange={e => {
                    setKind(e.target.value as LegalDocumentKind);
                    setPublishing(null);
                  }}
                >
                  <option value="terms">Terms of Use</option>
                  <option value="privacy">Privacy notice</option>
                  <option value="baa">Business Associate Agreement</option>
                </select>
              </label>
              <label className="block font-medium" htmlFor="document-audience">
                Audience
                <select
                  id="document-audience"
                  name="audience"
                  className={inputClass}
                  disabled={kind === 'baa'}
                  value={kind === 'baa' ? 'all' : audience}
                  onChange={e => setAudience(e.target.value as LegalDocumentAudience)}
                >
                  {kind === 'baa' ? (
                    <option value="all">All clinics</option>
                  ) : (
                    <>
                      <option value="essentials">Essentials</option>
                      <option value="ehr">EHR</option>
                    </>
                  )}
                </select>
              </label>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block font-medium" htmlFor="document-title">
                Display title
                <input
                  id="document-title"
                  name="title"
                  className={inputClass}
                  required
                  maxLength={200}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </label>
              <label className="block font-medium" htmlFor="document-version">
                Version label
                <input
                  id="document-version"
                  name="version"
                  className={inputClass}
                  required
                  maxLength={80}
                  placeholder="e.g. 2026-10"
                  value={version}
                  onChange={e => setVersion(e.target.value)}
                />
              </label>
            </div>
            <label className="block font-medium" htmlFor="document-summary">
              What changed? (shown to users)
              <textarea
                id="document-summary"
                name="changeSummary"
                className={inputClass}
                rows={3}
                maxLength={4000}
                required
                value={summary}
                onChange={e => setSummary(e.target.value)}
              />
            </label>
            {kind === 'baa' && (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="useExistingBaa"
                  className="mt-1 h-5 w-5 shrink-0 rounded"
                  checked={useBaa}
                  onChange={e => setUseBaa(e.target.checked)}
                />
                Use the existing ChiroUp BAA text, with the parties, dates, and electronic
                signatures recorded in the execution certificate.
              </label>
            )}
            {!(kind === 'baa' && useBaa) && (
              <label className="block font-medium" htmlFor="document-pdf">
                Final PDF (up to 2 MiB)
                <input
                  id="document-pdf"
                  name="pdf"
                  type="file"
                  accept="application/pdf,.pdf"
                  required
                  className={inputClass}
                  onChange={e => setFile(e.target.files?.[0] || null)}
                />
              </label>
            )}
            <button type="submit" className={buttonClass} disabled={busy}>
              {busy ? 'Saving…' : 'Save draft for review'}
            </button>
          </form>
        </section>
      )}
      <section className="mt-10">
        <h2 className="text-xl font-semibold">Version library</h2>
        {catalog.isLoading && <p className="mt-4">Loading versions…</p>}
        {catalog.data?.length === 0 && (
          <p className="mt-4">
            No documents published. Create and review the initial versions before enabling the new
            acceptance requirements.
          </p>
        )}
        <div className="mt-4 divide-y divide-gray-900/10 dark:divide-white/10">
          {catalog.data?.map(v => (
            <article key={v.id} className="py-5">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div className="min-w-0">
                  <h3 className="text-lg font-medium">
                    {v.title} · {v.version}
                  </h3>
                  <p className="mt-2">
                    {v.audience === 'all'
                      ? 'All clinics'
                      : v.audience === 'ehr'
                        ? 'EHR'
                        : 'Essentials'}{' '}
                    ·{' '}
                    {!v.publishedAt
                      ? 'Draft'
                      : v.effectiveAt! > Date.now()
                        ? 'Scheduled'
                        : 'Published'}
                    {v.effectiveAt
                      ? ` · Effective ${new Date(v.effectiveAt).toLocaleString()}`
                      : ''}
                  </p>
                  <p className="mt-2 max-w-prose whitespace-pre-wrap">{v.changeSummary}</p>
                  {v.publishedBy && (
                    <p className="mt-2 text-gray-600 dark:text-dark-gray-300">
                      Published by {v.publishedBy} ·{' '}
                      {v.materialChange ? 'New acceptance required' : 'Prior acceptance retained'} ·
                      Review: {v.legalReviewReference}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    className={linkClass}
                    onClick={async () => {
                      try {
                        await saveLegalPdf(await legalDocumentService.download(v.id));
                      } catch (err) {
                        setError(legalDocumentError(err));
                      }
                    }}
                  >
                    Download PDF
                  </button>
                  {!v.publishedAt && hasPermission('documents.publish') && (
                    <button type="button" className={buttonClass} onClick={() => setPublishing(v)}>
                      Review & publish
                    </button>
                  )}
                </div>
              </div>
              {publishing?.id === v.id && (
                <PublishDocument
                  key={v.id}
                  version={v}
                  onDone={() => {
                    setPublishing(null);
                    catalog.refetch();
                  }}
                />
              )}
            </article>
          ))}
        </div>
      </section>
      <section className="mt-10 border-t border-gray-900/10 pt-6 dark:border-white/10">
        <h2 className="text-xl font-semibold">Find clinic evidence</h2>
        <form
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={event => {
            event.preventDefault();
            setHistoryClinicId(Number(clinicId));
          }}
        >
          <label className="block font-medium" htmlFor="evidence-clinic-id">
            Clinic ID
            <input
              id="evidence-clinic-id"
              name="clinicId"
              type="number"
              min={1}
              step={1}
              required
              className={inputClass}
              value={clinicId}
              onChange={e => setClinicId(e.target.value)}
            />
          </label>
          <button type="submit" className={buttonClass}>
            View records
          </button>
        </form>
        {historyClinicId && (
          <ClinicDocumentHistory key={historyClinicId} clinicId={historyClinicId} />
        )}
      </section>
    </main>
  );
};

export default DocumentCenter;
